import {
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { StandstillExemption } from "../../../autogen/bff-api";
import { MAX_STANDSTILL_DAYS, useAwardSelection } from "./AwardSelectionContext";
import { REVEAL_ANCHORS } from "../revealAnchor";

export const StandstillChoiceField = ({
  withoutProvision = false,
  underPartIV = false,
  underUtilities = false,
}: {
  withoutProvision?: boolean;
  underPartIV?: boolean;
  underUtilities?: boolean;
}) => {
  const { t } = useTranslation();
  const {
    standstillDays,
    setStandstillDays,
    noStandstill,
    setNoStandstill,
    standstillExemption,
    setStandstillExemption,
    standstillProblem,
    minimumStandstillDays,
    ruledOutExemptions,
    winnerIds,
    tenderCheck,
    soleSupplierRefused,
    recheckTenders,
  } = useAwardSelection();
  const soleSupplierRuledOut = ruledOutExemptions.has(StandstillExemption.SoleConcernedSupplier);
  const callOffRuledOut = ruledOutExemptions.has(StandstillExemption.FrameworkAgreementCallOff);
  const offersCallOff = !underPartIV;
  const noExceptionApplies = soleSupplierRuledOut && (callOffRuledOut || !offersCallOff);
  const citesNoExceptionList = withoutProvision || underPartIV;
  const retry = (
    <Button variant="link" size="xs" colorScheme="teal" onClick={recheckTenders}>
      {t("Try again")}
    </Button>
  );
  const exceptionsUnchecked = winnerIds.length <= 1 && tenderCheck !== "CHECKED";
  const soleSupplierReason =
    winnerIds.length > 1 ? (
      t("More than one supplier is selected as the winner")
    ) : tenderCheck === "FAILED" ? (
      <>
        {t("We could not check which suppliers delivered tenders.")} {retry}
      </>
    ) : tenderCheck === "CHECKING" ? (
      t("Checking which suppliers delivered tenders…")
    ) : soleSupplierRefused ? (
      t("The exception was refused for this award. Set a standstill period in days.")
    ) : (
      t("Other suppliers delivered tenders, so the selected supplier is not the only one concerned.")
    );

  useEffect(() => {
    if (noExceptionApplies && noStandstill) setNoStandstill(false);
  }, [noExceptionApplies, noStandstill, setNoStandstill]);

  return (
    <Stack spacing="3" id={REVEAL_ANCHORS.standstill} sx={{ scrollMarginTop: "1rem" }}>
      <FormControl
        isInvalid={standstillProblem === "DAYS_NOT_A_WHOLE_NUMBER_IN_RANGE" || standstillProblem === "DAYS_BELOW_MINIMUM"}
        isDisabled={noStandstill}
      >
        <FormLabel>{t("Standstill period (days)")}</FormLabel>
        <Input
          inputMode="numeric"
          autoComplete="off"
          value={standstillDays}
          onChange={(e) => setStandstillDays(e.target.value)}
          bg="smBackground"
          maxW="32"
        />
        <FormHelperText>
          {withoutProvision
            ? t(
                "Part I sets no standstill period, but a reasonable one lets the suppliers complain before the contract is signed. The default is 10 days.",
              )
            : underPartIV && underUtilities
            ? t(
                "Part III of forsyningsforskriften requires a standstill period of at least 10 days (§ 26-8). The default is 10 days.",
              )
            : underPartIV
            ? t("Part IV requires a standstill period of at least 10 days (FOA § 30-9). The default is 10 days.")
            : t(
                "The standstill period must be reasonable (FOA § 10-2). The default is 10 days, the same as above the EEA threshold.",
              )}
        </FormHelperText>
        <FormErrorMessage>
          {standstillProblem === "DAYS_BELOW_MINIMUM" && underPartIV && underUtilities
            ? t("Part III of forsyningsforskriften needs a standstill period of at least {{days}} days (§ 26-8)", {
                days: minimumStandstillDays,
              })
            : standstillProblem === "DAYS_BELOW_MINIMUM" && underPartIV
            ? t("Part IV needs a standstill period of at least {{days}} days (FOA § 30-9)", {
                days: minimumStandstillDays,
              })
            : standstillProblem === "DAYS_BELOW_MINIMUM"
            ? t(
                "Special services over the EEA threshold need a standstill period of at least {{days}} days (FOA § 10-2 (2))",
                { days: minimumStandstillDays },
              )
            : t("Write a whole number of days from 1 to {{max}}", { max: MAX_STANDSTILL_DAYS })}
        </FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={standstillProblem === "EXEMPTION_MISSING"}>
        <Checkbox
          isChecked={noStandstill}
          isDisabled={noExceptionApplies}
          onChange={(e) => setNoStandstill(e.target.checked)}
        >
          {t("No standstill period")}
        </Checkbox>
        {noExceptionApplies && (
          <Text fontSize="sm" color="smMuted" ml="6">
            {!exceptionsUnchecked ? (
              <>
                {citesNoExceptionList
                  ? t("There is no reason to skip the standstill period")
                  : t("None of the exceptions in FOA § 10-2 (3) applies to this award")}
                : {soleSupplierReason}
              </>
            ) : tenderCheck === "FAILED" ? (
              <>
                {citesNoExceptionList
                  ? t("We could not check whether the selected supplier is the only one concerned.")
                  : t("We could not check whether the exceptions in FOA § 10-2 (3) apply.")}{" "}
                {retry}
              </>
            ) : (
              t("Checking which suppliers delivered tenders…")
            )}
          </Text>
        )}
        {noStandstill && (
          <RadioGroup
            value={standstillExemption ?? ""}
            onChange={(value) => setStandstillExemption(value as StandstillExemption)}
            mt="2"
            ml="6"
          >
            <Stack spacing="2">
              <Radio value={StandstillExemption.SoleConcernedSupplier} isDisabled={soleSupplierRuledOut}>
                {t("The selected supplier is the only supplier concerned")}
              </Radio>
              {soleSupplierRuledOut && (
                <Text fontSize="sm" color="smMuted" ml="6">
                  {soleSupplierReason}
                </Text>
              )}
              {offersCallOff && (
                <Radio value={StandstillExemption.FrameworkAgreementCallOff} isDisabled={callOffRuledOut}>
                  {t("The contract is awarded under a framework agreement")}
                </Radio>
              )}
              {offersCallOff && callOffRuledOut && (
                <Text fontSize="sm" color="smMuted" ml="6">
                  {t("Applies only to a call-off under an existing framework agreement, which this procurement is not")}
                </Text>
              )}
            </Stack>
          </RadioGroup>
        )}
        <FormErrorMessage ml="6">{t("Choose why there is no standstill period")}</FormErrorMessage>
      </FormControl>
    </Stack>
  );
};
